// =============================================================================
// AchievementCard: one card on the Refine tab's corpus canvas. Reads straight
// off apps/throughline/src/lib/api.ts's AchievementRecord — no reshaping, no
// invented fields (only what the server actually sent: headline, kpiLabel/
// Value, tags — every field optional-shows, matching apps/web's own
// AchievementCard.tsx, whose STAR fields "render a quiet on-ramp, never
// fabricated text" when absent, same law here).
//
// Draggable via native HTML5 drag (draggable attribute + onDragStart), NOT
// @dnd-kit — apps/web depends on @dnd-kit/core for its canvas, but adding that
// dependency to apps/throughline for one drag interaction is unwarranted
// weight for a scaffold-stage app; native drag-and-drop covers the single
// card-onto-role-header interaction this tab needs. If a future brief needs
// dnd-kit's richer sorting/collision behaviour here, add it then.
//
// LANE ROLE-REFINE adds the edit affordance (parity matrix gap #7): a pencil
// button flips the card into AchievementEditForm (headline/KPI/STAR fields),
// in place on the canvas — no modal, no navigation away from the card grid.
// Editing and dragging are mutually exclusive per card (the drag handle and
// edit button both hide while a rehome is pending) so the two optimistic-
// write paths (rehome vs field-edit) never race on the same card.
// =============================================================================

import { useState } from 'react';
import { Card } from './Card';
import clsx from './clsx';
import { AchievementEditForm } from './AchievementEditForm';
import { AxisAffinityChips } from './AxisAffinityChips';
import styles from './AchievementCard.module.css';
import { confirmAxisAffinity, removeAxisAffinity } from '../lib/api';
import type { AchievementPatch, AchievementRecord } from '../lib/api';

export interface AchievementCardProps {
  achievement: AchievementRecord;
  /** True while this specific card is the one being dragged (drag-source
   *  visual state). */
  dragging?: boolean;
  /** True while a rehome for this card is in flight (disables further drags
   *  on it until the server confirms or rolls back). */
  pending?: boolean;
  onDragStart?: (id: string) => void;
  onDragEnd?: () => void;
  /** Present -> the card can be edited (the pencil button renders). Absent ->
   *  read-only (mirrors RoleGroupSection's own onToggleResume optionality —
   *  a caller that doesn't wire editing gets byte-identical behaviour to
   *  before this lane). Returns the honest error message on failure (the
   *  caller has already applied the server-confirmed rollback), or null on
   *  success — same contract as useCorpusCanvas's rehome(). */
  onSaveEdit?: (id: string, patch: AchievementPatch) => Promise<string | null>;
  /** True while THIS card's field-edit save is in flight. */
  savingEdit?: boolean;
}

export function AchievementCard({
  achievement,
  dragging = false,
  pending = false,
  onDragStart,
  onDragEnd,
  onSaveEdit,
  savingEdit = false,
}: AchievementCardProps) {
  const [editing, setEditing] = useState(false);
  // AXIS-P2: local mirror of the served axisAffinities so a confirm/remove
  // tap can update the chip row immediately without re-fetching the whole
  // card list (served-data-only: this reconciles what the confirm/remove
  // route just returned, never a client-computed affinity). Re-seeded via
  // useState's lazy-init + a prop-tracking ref: when the PARENT hands this
  // component a genuinely new served array (a re-fetch, or a different
  // card's props landing on the same mounted instance because the parent
  // list didn't remount it), the local mirror re-syncs to the new source.
  const [affinitiesSource, setAffinitiesSource] = useState(achievement.axisAffinities ?? null);
  const [affinities, setAffinities] = useState(achievement.axisAffinities ?? []);
  if (achievement.axisAffinities !== affinitiesSource) {
    setAffinitiesSource(achievement.axisAffinities ?? null);
    setAffinities(achievement.axisAffinities ?? []);
  }
  const hasKpi = Boolean(achievement.kpiLabel && achievement.kpiValue);
  const tags = achievement.tags ?? [];
  const canEdit = onSaveEdit != null && !pending;

  if (editing) {
    return (
      <Card className={clsx(styles.card, styles.editingCard)}>
        <AchievementEditForm
          achievement={achievement}
          saving={savingEdit}
          onSave={async (patch) => {
            const err = await onSaveEdit!(achievement.id, patch);
            if (!err) setEditing(false);
            return err;
          }}
          onCancel={() => setEditing(false)}
        />
      </Card>
    );
  }

  return (
    <Card
      className={clsx(styles.card, dragging && styles.dragging, pending && styles.pending)}
    >
      <div
        className={styles.dragHandle}
        draggable={!pending}
        onDragStart={(e) => {
          e.dataTransfer.setData('text/plain', achievement.id);
          e.dataTransfer.effectAllowed = 'move';
          onDragStart?.(achievement.id);
        }}
        onDragEnd={() => onDragEnd?.()}
        aria-label={`Drag "${achievement.headline}" to a different role`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="9" cy="6" r="1.4" fill="currentColor" />
          <circle cx="15" cy="6" r="1.4" fill="currentColor" />
          <circle cx="9" cy="12" r="1.4" fill="currentColor" />
          <circle cx="15" cy="12" r="1.4" fill="currentColor" />
          <circle cx="9" cy="18" r="1.4" fill="currentColor" />
          <circle cx="15" cy="18" r="1.4" fill="currentColor" />
        </svg>
      </div>

      {canEdit ? (
        <button
          type="button"
          className={styles.editButton}
          onClick={() => setEditing(true)}
          aria-label={`Edit "${achievement.headline}"`}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : null}

      <div className={styles.headline}>{achievement.headline}</div>

      {hasKpi ? (
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>{achievement.kpiLabel}</span>
          <span className={styles.kpiValue}>{achievement.kpiValue}</span>
        </div>
      ) : null}

      {tags.length > 0 ? (
        <div className={styles.tagRow}>
          {tags.map((tag) => (
            <span key={tag} className={styles.tag}>
              {tag}
            </span>
          ))}
        </div>
      ) : null}

      <AxisAffinityChips
        affinities={affinities}
        subjectType="achievement"
        onConfirm={
          canEdit
            ? async (axisKey) => {
                try {
                  const confirmed = await confirmAxisAffinity(achievement.id, 'achievement', axisKey);
                  setAffinities((prev) =>
                    prev.map((a) => (a.axisKey === axisKey ? confirmed : a)),
                  );
                  return null;
                } catch (err) {
                  return err instanceof Error ? err.message : 'Could not confirm this tag.';
                }
              }
            : undefined
        }
        onRemove={
          canEdit
            ? async (axisKey) => {
                try {
                  await removeAxisAffinity(achievement.id, 'achievement', axisKey);
                  setAffinities((prev) => prev.filter((a) => a.axisKey !== axisKey));
                  return null;
                } catch (err) {
                  return err instanceof Error ? err.message : 'Could not remove this tag.';
                }
              }
            : undefined
        }
      />

      {achievement.voiceNoteR2Key ? (
        <div className={styles.voiceNoteBadge}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="2" />
            <path
              d="M5 11a7 7 0 0 0 14 0M12 18v3"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          Captured by voice
        </div>
      ) : null}

      {pending ? <div className={styles.pendingNote}>Moving…</div> : null}
    </Card>
  );
}
